import { curWeight, strideMeters, walkMet } from "./energy";
import { MODERATE_CADENCE } from "./movement";
import type { KayaState, WalkLog } from "./types";

/**
 * A free walk, as opposed to the treadmill protocol.
 *
 * The honest constraint here: **a browser cannot count steps.** On the treadmill the machine's
 * speed gives us distance directly, so a live step figure is real. On a free walk there is no
 * speed, so during the walk we can only show time and a calorie estimate from a nominal pace —
 * the step count and the distance arrive afterwards, from the phone's own counter.
 *
 * That is why the walk player shows a stopwatch rather than pretending to count.
 */

/** A steady walk, used only until the real pace can be derived from measured steps. */
export const NOMINAL_WALK_MET = 3.3;

export interface WalkTotals {
  kcal: number;
  /** null while unknown — a walk with no measurement has no defensible distance */
  km: number | null;
  steps: number | null;
}

export function walkTotals(S: KayaState, secs: number, measuredSteps?: number | null): WalkTotals {
  const hours = secs / 3600;

  if (typeof measuredSteps === "number" && measuredSteps > 0) {
    // With real steps we can work backwards to the actual pace and price the walk properly,
    // instead of assuming everyone strolls at the same speed.
    const km = (measuredSteps * strideMeters(S)) / 1000;
    const speed = hours > 0 ? km / hours : 0;
    return { kcal: Math.round(walkMet(speed) * curWeight(S) * hours), km, steps: measuredSteps };
  }

  return { kcal: Math.round(NOMINAL_WALK_MET * curWeight(S) * hours), km: null, steps: null };
}

/** Everything walked that day, for the day's totals. */
export function walkSecs(walks?: WalkLog[]): number {
  return (walks || []).reduce((a, w) => a + w.secs, 0);
}

export function walkKcal(walks?: WalkLog[]): number {
  return (walks || []).reduce((a, w) => a + (w.kcal || 0), 0);
}

/**
 * Steps the phone measured across the day's walks.
 *
 * These are deliberately NOT added to the day's step total. A walk is counted by the phone in your
 * pocket as it happens, so those steps are already inside the daily figure — adding them again
 * would double-count. The treadmill is the exception, and only because the phone may be sitting on
 * the console instead of on you.
 */
export function walkMeasuredSteps(walks?: WalkLog[]): number {
  return (walks || []).reduce((a, w) => a + (w.measuredSteps || 0), 0);
}

/* ---------- cadence: "was that brisk?" (ADR-063) ---------- */

/** Under two minutes the figure is noise: the first ten-second reading and a pause at a crossing
 *  can halve or double it. */
export const CADENCE_MIN_SECS = 120;
/** CADENCE-Adults (61–85): about 130 steps a minute is vigorous; 100 is moderate (`movement.ts`). */
export const VIGOROUS_CADENCE = 130;

/**
 * Steps a minute over the walk's active time, or null: unmeasured, under two minutes, or a measured
 * zero — the phone was not on you, which says nothing about the pace (invariant 11).
 */
export function walkCadence(w: Pick<WalkLog, "measuredSteps" | "secs">): number | null {
  if (typeof w.measuredSteps !== "number" || w.measuredSteps <= 0) return null;
  if (!(w.secs >= CADENCE_MIN_SECS)) return null;
  return Math.round(w.measuredSteps / (w.secs / 60));
}

/** The word beside a cadence — never a grade, never "slow". */
export function cadenceWord(spm: number): "an easy pace" | "brisk" | "fast" {
  if (spm >= VIGOROUS_CADENCE) return "fast";
  if (spm >= MODERATE_CADENCE) return "brisk";
  return "an easy pace";
}

/** "105 a minute · brisk" for a row; null when there is no cadence to show. */
export function cadenceLine(w: Pick<WalkLog, "measuredSteps" | "secs">): string | null {
  const spm = walkCadence(w);
  return spm === null ? null : `${spm} a minute · ${cadenceWord(spm)}`;
}
